import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import BookingFilterRaw from "./BookingFilter";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import BookingList from "./BookingList";
import styled from "styled-components";
import DeleteBooking from "../DeleteBooking";
import MyBookingMultiDeleteDialog from "../booking-instruments/my-bookings/my-booking-list-view/my-booking-multi-delete-button/MyBookingMultiDeleteDialog";
import BookingNotificationStatus from "../booking-calendars/booking-notification-status/BookingNotificationStatus";
import { timeoutCallbackFactory } from "../booking-calendars/booking-notification-status/timeoutCallbackFactory";
import {
  allTableColumn,
  DEFAULT_COLUMNS,
  DEFAULT_FILTER,
  EQUIPMENT_BASED_DEFAULT_SHOW_COLUMNS,
  FORCE_TIMELINE_RANGE_KEY,
  GRID_VIEW,
  LIST_VIEW,
  LIST_GROUP_BY_BOOKING,
  LIST_GROUP_BY_EQUIPMENT,
  LIST_GROUP_BY_NONE,
  TIMELINE_VIEW,
  MY_LAB,
} from "../../constants";
import { filter, find, keyBy, sortBy } from "lodash";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { useSelector } from "react-redux";
import { convertStrToObjColumns, convertToCameCase, get4WeekStartAndEndInISOString } from "../../utils/helpers/text";
import BookingGrid from "./BookingGrid";
import { MemoizedEquipmentBookingGrid as EquipmentBookingGrid } from "../bookings/MemoizedComponents";
import { useParams } from "react-router-dom";
import {
  MY_BOOKING_STORAGE_KEYS,
  getPersistedFilter,
  setPersistedFilter,
} from "../../utils/store/bookingFilterPersistence";
import { generateViewKey, resetViewState } from "../bookings/ViewSwitchingUtils";
import EquipmentBookingList from "./EquipmentBookingList";
import { withApollo } from "react-apollo";
import { SharedFilterProvider, useSharedFilterState } from "../bookings/SharedFilterContext";
import { getColumnObj, rehydrateStoredColumn } from "../equipment-list/EquipmentHelper";
import { loadEquipmentGridColumns } from "../schema/helpers/equipmentGridColumns";
import { quickEditTracker } from "../booking-operations/quickEditTracker";
import { BOOKING_OPERATIONS, useBookingOperations } from "../booking-operations";
import { getSubscriptionOperation } from "../bookings/Bookings.utils";
import moment from "moment";
const autoGroupColField = "equipment_model";
const SCHEMA_LOAD_DELAY_MS = 2000;

const BookingFilter = React.memo(BookingFilterRaw, (prev, next) => {
  const keys = [
    "resetAll", "showAdvanceFilter", "disableResetAll", "searchQuery",
    "view", "isEquipmentView", "savedFilter", "showFilters",
    "listGroupByView", "displayPreset", "showSideBar", "currentSideBarPanel",
    "isFilterActive", "isColumnActive", "isGroupView", "groupName",
    "userFilters", "myLabFilter", "userDisplayColumnMaster", "tempRowData",
    "showInvalidFilter",
  ];
  for (const k of keys) {
    if (prev[k] !== next[k]) {
      return false;
    }
  }
  if (JSON.stringify(prev.selectedDateRange) !== JSON.stringify(next.selectedDateRange)) {
    return false;
  }
  if (prev.signeeFilter?.userId !== next.signeeFilter?.userId) {
    return false;
  }
  return true;
});

const MainViewStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;
  padding-top: 8px;
  padding-bottom: 8px;
`;

export const createResetStates = ({ setOpenMultiDeleteDialog, setStatus, setIsDropBooking, setIsDialogOpen }) => ({
  multiDeleteDialog: setOpenMultiDeleteDialog,
  status: setStatus,
  dropBooking: setIsDropBooking,
  dialog: setIsDialogOpen,
});

export const createOnSuccessHandler =
  ({ dispatchAction }) =>
  () => {
    if (dispatchAction) {
      dispatchAction({ type: "fetching", payload: false });
      dispatchAction({ type: "refresh" });
    }
  };

export const createTriggerRefreshHandler =
  ({ triggerFetch }) =>
  () => {
    triggerFetch(false);
    triggerFetch(true);
  };

export const createMyBookingSubscriptionCallback = ({
  createBookingCallback,
  triggerFetch,
  dispatchAction,
  setOpenMultiDeleteDialog,
  setStatus,
  setDropType,
  setIsDropBooking,
  setIsDialogOpen,
  setTempRowData,
  quickEditTracker: quickEditTrackerArg = quickEditTracker,
  BOOKING_OPERATIONS: bookingOperationsArg = BOOKING_OPERATIONS,
}) => {
  const resetStates = createResetStates({
    setOpenMultiDeleteDialog,
    setStatus,
    setIsDropBooking,
    setIsDialogOpen,
  });
  const onSuccess = createOnSuccessHandler({ dispatchAction });
  const triggerRefresh = createTriggerRefreshHandler({ triggerFetch });

  return (data) => {
    const subscriptionData = data?.data?.onUpdateBookingCommand;
    if (!subscriptionData) {
      return;
    }

    const operation = getSubscriptionOperation({
      processingResult: subscriptionData.processingResult,
      commandId: subscriptionData.id,
      commandType: subscriptionData.commandType,
      quickEditTracker: quickEditTrackerArg,
      BOOKING_OPERATIONS: bookingOperationsArg,
    });

    const callback = createBookingCallback({
      operation,
      resetStates,
      clearSelections: [setTempRowData],
      triggerRefresh,
      onSuccess,
    });

    callback(data);
  };
};

function MyBooking({ client, ...rest }) {
  const user = useSelector((store) => store.user);
  return (
    <SharedFilterProvider client={client} user={user}>
      <MyBookingInner client={client} {...rest} />
    </SharedFilterProvider>
  );
}

function MyBookingInner({
  subscriptionStatus,
  setSubscriptionStatus,
  setPopupEnableStatus,
  client,
  groupName,
  isGroupView,
}) {
  const getListModePreferenceKey = (groupByView) =>
    groupByView === LIST_GROUP_BY_BOOKING ? LIST_GROUP_BY_BOOKING : LIST_GROUP_BY_NONE;

  const normalizeSavedAdvanceFilter = (savedValue) => {
    if (savedValue && typeof savedValue === "object") {
      return {
        [LIST_GROUP_BY_BOOKING]: savedValue[LIST_GROUP_BY_BOOKING] ?? false,
        [LIST_GROUP_BY_NONE]: savedValue[LIST_GROUP_BY_NONE] ?? true,
      };
    }

    const fallback = typeof savedValue === "boolean" ? savedValue : true;
    return {
      [LIST_GROUP_BY_BOOKING]: fallback,
      [LIST_GROUP_BY_NONE]: fallback,
    };
  };

  const { trackPageView } = useMatomo();
  const { EventID } = useParams();
  const [eventID, setEventID] = useState(null);

  useEffect(() => {
    trackPageView({
      documentTitle: "My bookings",
      href: `${window.location?.origin}/Mybookings`,
    });
    if (EventID) {
      setEventID(EventID);
      setView(GRID_VIEW);
    }
    // eslint-disable-next-line
  }, []);
  const user = useSelector((store) => store.user);
  const storageOptions = useMemo(() => ({ namespace: "my_bookings" }), []);
  const [resetAll, setResetAll] = useState(false);
  const [disableResetAll, setDisableResetAll] = useState(true);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [searchQuery, setSearchQuery] = useState(() => {
    return getPersistedFilter(MY_BOOKING_STORAGE_KEYS.SEARCH_QUERY, "", storageOptions);
  });

  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const defaultDateRange = [
      get4WeekStartAndEndInISOString("start", user.timezone),
      get4WeekStartAndEndInISOString("end", user.timezone),
    ];
    try {
      const raw = sessionStorage.getItem(FORCE_TIMELINE_RANGE_KEY);
      if (!raw) {
        const persistedDateRange = getPersistedFilter(
          MY_BOOKING_STORAGE_KEYS.SELECTED_DATE_RANGE,
          null,
          storageOptions,
        );
        if (Array.isArray(persistedDateRange) && persistedDateRange.length === 2) {
          return persistedDateRange;
        }
        return defaultDateRange;
      }
      const { dateFrom, dateTo } = JSON.parse(raw) ?? {};
      if (!dateFrom || !dateTo) return defaultDateRange;
      const tz = user?.timezone || "UTC";
      const fromStr = moment(dateFrom).tz(tz).format();
      const toStr = moment(dateTo).tz(tz).format();
      setTimeout(() => sessionStorage.removeItem(FORCE_TIMELINE_RANGE_KEY), 1000);
      return [fromStr, toStr];
    } catch (e) {
      const persistedDateRange = getPersistedFilter(
        MY_BOOKING_STORAGE_KEYS.SELECTED_DATE_RANGE,
        defaultDateRange,
        storageOptions,
      );
      if (Array.isArray(persistedDateRange) && persistedDateRange.length === 2) {
        return persistedDateRange;
      }
      return defaultDateRange;
    }
  });

  const [tempRowData, setTempRowData] = useState([]);
  const [dropType, setDropType] = useState("cancel"); // Default drop type
  const [isDropBooking, setIsDropBooking] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [openMultiDeleteDialog, setOpenMultiDeleteDialog] = useState(false);
  const [rowEquipmentId, setRowEquipmentId] = useState(null);
  const [shouldFetchData, triggerFetch] = useState(false);

  const { dispatchAction } = useContext(PaginationContext);
  const [status, setStatus] = useState(false);
  const { createBookingCallback } = useBookingOperations();

  const [view, setView] = useState(() => {
    return getPersistedFilter(MY_BOOKING_STORAGE_KEYS.VIEW, GRID_VIEW, storageOptions);
  });
  const [showAdvanceFilterByList, setShowAdvanceFilterByList] = useState(() => {
    const savedValue = getPersistedFilter(MY_BOOKING_STORAGE_KEYS.SHOW_ADVANCE_FILTER, null, storageOptions);
    return normalizeSavedAdvanceFilter(savedValue);
  });
  const [isEquipmentView, setIsEquipmentView] = useState(() => {
    return getPersistedFilter(MY_BOOKING_STORAGE_KEYS.IS_EQUIPMENT_VIEW, false, storageOptions);
  });
  const [displayPreset, setDisplayPreset] = useState(() => {
    return getPersistedFilter(MY_BOOKING_STORAGE_KEYS.DISPLAY_PRESET, null, storageOptions);
  });
  const [listGroupByView, setListGroupByView] = useState(() => {
    return getPersistedFilter(MY_BOOKING_STORAGE_KEYS.LIST_GROUP_BY_VIEW, LIST_GROUP_BY_BOOKING, storageOptions);
  });
  const [savedFilter, setSavedFilter] = useState(() => {
    return isGroupView ? groupName : getPersistedFilter(MY_BOOKING_STORAGE_KEYS.SAVED_FILTER, MY_LAB, storageOptions);
  });
  const [signeeFilter, setSigneeFilter] = useState(() => {
    return getPersistedFilter(MY_BOOKING_STORAGE_KEYS.SIGNEE_FILTER, "", storageOptions);
  });
  const [showFilters, setShowFilters] = useState(() => {
    return getPersistedFilter(MY_BOOKING_STORAGE_KEYS.SHOW_FILTERS, true, storageOptions);
  });
  const [innerTableFiltersByList, setInnerTableFiltersByList] = useState({});

  const [showInvalidFilter, setShowInvalidFilter] = useState(false);
  const [showSideBar, setShowSideBar] = useState(false);
  const [currentSideBarPanel, setCurrentSideBarPanel] = useState("");
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isColumnActive, setIsColumnActive] = useState(false);
  const isListGroupedByNone = listGroupByView === LIST_GROUP_BY_NONE || listGroupByView === LIST_GROUP_BY_EQUIPMENT;
  const listModePreferenceKey = getListModePreferenceKey(listGroupByView);
  const showAdvanceFilter = showAdvanceFilterByList[listModePreferenceKey] ?? true;
  const setShowAdvanceFilter = (nextValue) => {
    setShowAdvanceFilterByList((prevState) => {
      const previousValue = prevState[listModePreferenceKey] ?? true;
      const resolvedValue = typeof nextValue === "function" ? nextValue(previousValue) : nextValue;

      return {
        ...prevState,
        [listModePreferenceKey]: resolvedValue,
      };
    });
  };

  const getDefaultEquipmentBasedColumns = () => {
    if (!isListGroupedByNone) return DEFAULT_COLUMNS();
    const defaultCols = structuredClone(DEFAULT_COLUMNS());
    defaultCols.displayDefinition.showColumns = EQUIPMENT_BASED_DEFAULT_SHOW_COLUMNS;
    return defaultCols;
  };
  const [dataModel, setDataModel] = useState([]);
  const {
    myLabFilter,
    userFilters: sharedUserFilters,
    userDisplayColumnMaster,
    userDetail: sharedUserDetail,
    masterDataFilters,
    isFilterDataReady: sharedFilterReady,
    loadFilterData: sharedLoadFilterData,
  } = useSharedFilterState();

  const [userFilters, setUserFilters] = useState([]);
  const localUserFilters = useRef(userFilters);
  const [lastFavFilter, setLastFavFilter] = useState(DEFAULT_FILTER);
  const [lastDisplayColumns, setLastDisplayColumns] = useState(() => getDefaultEquipmentBasedColumns());
  const [defaultShowColumns, setDefaultShowColumns] = useState(() => getDefaultEquipmentBasedColumns());

  // Effect to handle invalid filter display with delay
  useEffect(() => {
    if (groupName !== savedFilter && isGroupView) {
      const timeoutId = setTimeout(() => {
        setShowInvalidFilter(true);
      }, 2000); // 1 second delay

      return () => clearTimeout(timeoutId);
    } else {
      setShowInvalidFilter(false);
    }
  }, [groupName, savedFilter, isGroupView]);

  // Persist full tab state immediately so it survives route changes reliably.
  useEffect(() => {
    if (isGroupView) {
      return;
    }

    const filtersToSave = {
      [MY_BOOKING_STORAGE_KEYS.SAVED_FILTER]: savedFilter,
      [MY_BOOKING_STORAGE_KEYS.SIGNEE_FILTER]: signeeFilter,
      [MY_BOOKING_STORAGE_KEYS.SHOW_FILTERS]: showFilters,
      [MY_BOOKING_STORAGE_KEYS.SHOW_ADVANCE_FILTER]: showAdvanceFilterByList,
      [MY_BOOKING_STORAGE_KEYS.SEARCH_QUERY]: searchQuery,
      [MY_BOOKING_STORAGE_KEYS.VIEW]: view,
      [MY_BOOKING_STORAGE_KEYS.IS_EQUIPMENT_VIEW]: isEquipmentView,
      [MY_BOOKING_STORAGE_KEYS.LIST_GROUP_BY_VIEW]: listGroupByView,
      [MY_BOOKING_STORAGE_KEYS.DISPLAY_PRESET]: displayPreset,
    };

    Object.entries(filtersToSave).forEach(([key, value]) => {
      try {
        setPersistedFilter(key, value, storageOptions);
      } catch (error) {
        console.warn("Failed to persist filter:", key, error);
      }
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    savedFilter,
    signeeFilter,
    showFilters,
    showAdvanceFilterByList,
    searchQuery,
    selectedDateRange,
    view,
    isEquipmentView,
    displayPreset,
    listGroupByView,
    isGroupView,
    storageOptions,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadEquipmentGridColumns().catch((error) => {
        // eslint-disable-next-line no-console
        console.error(
          "[schema] Could not derive grid columns; falling back to the hand-written list. " +
            "Schema-only columns (Description, Equipment type, …) will be missing.",
          error,
        );
      });
    }, SCHEMA_LOAD_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    sharedLoadFilterData();
  }, [sharedLoadFilterData]);

  useEffect(() => {
    if (!sharedFilterReady) {
      return;
    }

    setLastFavFilter(() => sharedUserDetail?.lastFavFilter);
    setLastDisplayColumns(() => sharedUserDetail?.lastDisplayColumns);
    const withFilterId = (sharedUserFilters || []).map((f) => {
      return {
        ...f,
        filterDefinition: {
          ...f?.filterDefinition,
          filterId: `f_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        },
      };
    });

    setUserFilters(withFilterId);

    const defaultShowCols = filter(allTableColumn(isListGroupedByNone), { show: true });

    setDefaultShowColumns(() => {
      return defaultShowCols?.map((value) => {
        return getColumnObj(value, [], convertToCameCase(autoGroupColField));
      });
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedFilterReady, sharedUserDetail, sharedUserFilters]);

  const bookingCallback = useMemo(
    () =>
      createMyBookingSubscriptionCallback({
        createBookingCallback,
        triggerFetch,
        dispatchAction,
        setOpenMultiDeleteDialog,
        setStatus,
        setIsDropBooking,
        setIsDialogOpen,
        setTempRowData,
        quickEditTracker,
        BOOKING_OPERATIONS,
      }),
    [createBookingCallback, dispatchAction],
  );

  useEffect(() => {
    if (subscriptionStatus) {
      const callback = createBookingCallback({
        operation: BOOKING_OPERATIONS.CREATE,
        debounce: false,
      });

      callback({
        data: {
          onUpdateBookingCommand: subscriptionStatus,
        },
      });

      setSubscriptionStatus(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionStatus]);

  useEffect(() => {
    // Reset state when switching views to ensure clean data load
    resetViewState(
      {
        setTempRowData,
        setSelectedEquipment,
      },
      dispatchAction,
    );

    // Log view switch for debugging
    // logViewSwitch(view, isEquipmentView, savedFilter, signeeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    isEquipmentView,
    listGroupByView,
    savedFilter,
    signeeFilter?.userId,
    setTempRowData,
    setSelectedEquipment,
    dispatchAction,
  ]);

  useEffect(() => {
    const unorderedHidenColumn = [];
    const orderedShowColumns = sortBy(
      convertStrToObjColumns(lastDisplayColumns)?.displayDefinition?.showColumns,
      ["order"],
      ["asc"],
    );

    allTableColumn(isListGroupedByNone)?.forEach((value, _index) => {
      const obj = find(orderedShowColumns, {
        key: value?.key,
      });
      if (!obj) {
        unorderedHidenColumn.push(value);
      }
    });

    // A stored entry is a layout record, not a column definition — see rehydrateStoredColumn.
    const schemaColumns = keyBy(allTableColumn(isListGroupedByNone), "key");

    const redefinedColumns = [...orderedShowColumns, ...unorderedHidenColumn]?.map((value) => {
      return getColumnObj(
        rehydrateStoredColumn(schemaColumns, value),
        masterDataFilters,
        convertToCameCase(autoGroupColField),
      );
    });
    const defaultShowCols = filter(allTableColumn(isListGroupedByNone), { show: true });
    setDefaultShowColumns(() => {
      return defaultShowCols?.map((value) => {
        return getColumnObj(value, masterDataFilters, convertToCameCase(autoGroupColField));
      });
    });
    setDataModel([...redefinedColumns]);
    // eslint-disable-next-line
  }, [isListGroupedByNone, lastDisplayColumns, masterDataFilters]);

  const listViewDisplay = () => {
    const innerTableFilterKey = getListModePreferenceKey(listGroupByView);
    const dateKey =
      selectedDateRange?.length === 2
        ? `${new Date(selectedDateRange[0]).toISOString()}_${new Date(selectedDateRange[1]).toISOString()}`
        : "no-range";
    const componentKey = generateViewKey(
      view,
      true,
      savedFilter,
      signeeFilter?.userId || "all",
      listGroupByView,
      displayPreset?.label,
      dateKey,
    );
    const listCommonProps = {
      selectedDateRange,
      setDisableResetAll,
      setSelectedDateRange,
      setSelectedEquipment,
      selectedEquipment,
      showAdvanceFilter,
      resetAll,
      setSearchQuery,
      searchQuery,
      setResetAll,
      setDropType,
      setIsDropBooking,
      setIsDialogOpen,
      setOpenMultiDeleteDialog,
      setRowEquipmentId,
      tempRowData,
      setTempRowData,
      setPopupEnableStatus,
      savedFilter,
      setSavedFilter,
      signeeFilter,
      setSigneeFilter,
      displayPreset,
      dataModel,
      setDisplayPreset,
      defaultShowColumns,
      shouldFetchData,
      userFilters,
      setUserFilters,
      lastDisplayColumns,
      setLastDisplayColumns,
      lastFavFilter,
      setLastFavFilter,
      localUserFilters,
      myLabFilter,
      setShowSideBar,
      showSideBar,
      currentSideBarPanel,
      setCurrentSideBarPanel,
      isFilterActive,
      setIsFilterActive,
      innerTableFilters: innerTableFiltersByList?.[innerTableFilterKey] || {},
      setInnerTableFilters: (nextFilters) => {
        setInnerTableFiltersByList((prev) => ({
          ...(prev || {}),
          [innerTableFilterKey]: nextFilters || {},
        }));
      },
    };
    if (listGroupByView === LIST_GROUP_BY_NONE || listGroupByView === LIST_GROUP_BY_EQUIPMENT) {
      return <EquipmentBookingList key={componentKey} {...listCommonProps} />;
    }
    return <BookingList key={componentKey} {...listCommonProps} />;
  };

  const isListGroupedByBookingView = view === LIST_VIEW && listGroupByView === LIST_GROUP_BY_BOOKING;
  const isListGroupedByNoneView =
    view === LIST_VIEW && (listGroupByView === LIST_GROUP_BY_NONE || listGroupByView === LIST_GROUP_BY_EQUIPMENT);
  const isAnyListView = isListGroupedByBookingView || isListGroupedByNoneView;

  const renderSelectedView = () => {
    if (isAnyListView) {
      return listViewDisplay();
    }

    if (view === TIMELINE_VIEW) {
      return (
        <EquipmentBookingGrid
          viewData={view}
          setViewData={setView}
          eventID={eventID}
          isEquipmentView={isEquipmentView}
          isTimelineView={true}
          setPopupEnableStatus={setPopupEnableStatus}
          savedFilter={savedFilter}
          setSavedFilter={setSavedFilter}
          signeeFilter={signeeFilter}
          selectedDateRange={selectedDateRange}
          isGroupView={isGroupView}
          listGroupByView={listGroupByView}
          setListGroupByView={setListGroupByView}
          timelineGroupByDefault={"category"}
          timelineGroupByStorageKey={MY_BOOKING_STORAGE_KEYS.TIMELINE_GROUP_BY_VIEW}
          timelineTimeframeStorageKey={MY_BOOKING_STORAGE_KEYS.TIMELINE_TIMEFRAME_VIEW}
          timelineStorageOptions={storageOptions}
          myBookingsMode={true}
        />
      );
    }

    return (
      <BookingGrid
        viewData={view}
        setViewData={setView}
        eventID={eventID}
        shouldFetchData={shouldFetchData}
        isEquipmentView={isEquipmentView}
        setIsEquipmentView={setIsEquipmentView}
        listGroupByView={listGroupByView}
        setListGroupByView={setListGroupByView}
        savedFilter={savedFilter}
        setSavedFilter={setSavedFilter}
      />
    );
  };

  return (
      <MainViewStyled>
        <>
          <BookingFilter
            resetAll={resetAll}
            setResetAll={setResetAll}
            showAdvanceFilter={showAdvanceFilter}
            setShowAdvanceFilter={setShowAdvanceFilter}
            setSelectedEquipment={setSelectedEquipment}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            tempRowData={tempRowData}
            setTempRowData={setTempRowData}
            disableResetAll={disableResetAll}
            setDisableResetAll={setDisableResetAll}
            view={view}
            setView={setView}
            isEquipmentView={isEquipmentView}
            setIsEquipmentView={setIsEquipmentView}
            savedFilter={savedFilter}
            setSavedFilter={setSavedFilter}
            displayPreset={displayPreset}
            setDisplayPreset={setDisplayPreset}
            listGroupByView={listGroupByView}
            setListGroupByView={setListGroupByView}
            signeeFilter={signeeFilter}
            setSigneeFilter={setSigneeFilter}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            groupName={groupName}
            isGroupView={isGroupView}
            showInvalidFilter={showInvalidFilter}
            userFilters={userFilters}
            userDisplayColumnMaster={userDisplayColumnMaster}
            setInnerTableFiltersByList={setInnerTableFiltersByList}
            myLabFilter={myLabFilter}
            showSideBar={showSideBar}
            setShowSideBar={setShowSideBar}
            currentSideBarPanel={currentSideBarPanel}
            setCurrentSideBarPanel={setCurrentSideBarPanel}
            isFilterActive={isFilterActive}
            setIsFilterActive={setIsFilterActive}
            isColumnActive={isColumnActive}
            setIsColumnActive={setIsColumnActive}
          />

          {renderSelectedView()}

          <BookingNotificationStatus
            tryTimes={3}
            timeout={5 * 60 * 1000}
            onTimeoutCallback={timeoutCallbackFactory()}
            onBookingCallback={bookingCallback}
          />

          {isDropBooking && (
            <DeleteBooking
              dropType={dropType}
              setIsDropBooking={setIsDropBooking}
              setIsDialogOpen={setIsDialogOpen}
              isDialogOpen={isDialogOpen}
              item={rowEquipmentId}
              status={status}
              setStatus={setStatus}
              triggerFetch={triggerFetch}
            />
          )}

          {openMultiDeleteDialog && (
            <MyBookingMultiDeleteDialog
              openMultiDeleteDialog={openMultiDeleteDialog}
              setOpenMultiDeleteDialog={setOpenMultiDeleteDialog}
              selectedEquipment={selectedEquipment}
              setSelectedEquipment={setSelectedEquipment}
              status={status}
              setStatus={setStatus}
            />
          )}
        </>
      </MainViewStyled>
  );
}

export default withApollo(MyBooking);
